import { CreditCard } from "lucide-react";
import { ApiV2Error } from "../../lib/api-v2";
import { cn } from "../../lib/utils";
import { rupees } from "../products/types";
import { formatWhen } from "./format-when";
import { usePaymentBreakup } from "./queries";
import type { PaymentAttemptStatus, PaymentBreakup } from "./types";

const STATUS: Record<PaymentAttemptStatus, { label: string; tone: string }> = {
  successful: { label: "Successful", tone: "border-success/30 bg-success-soft text-success" },
  failed: { label: "Failed", tone: "border-danger/30 bg-danger-soft text-danger" },
  pending: { label: "Pending", tone: "border-warning/30 bg-warning-soft text-warning" },
};

const method = (attempt: PaymentBreakup) =>
  attempt.method ? attempt.method.toUpperCase() : "Payment";

/** How the order was paid: every payment attempt, and how each splits over the items. */
export function PaymentDetails({ orderId }: { orderId: string }) {
  const breakup = usePaymentBreakup(orderId);

  return (
    <section className="space-y-4 rounded-2xl border border-line bg-elevated p-6 shadow-sm" aria-label="Payment details">
      <div className="flex items-center gap-2">
        <CreditCard className="h-5 w-5 text-ink-3" />
        <h2 className="text-lg font-semibold text-ink">Payment details</h2>
      </div>

      {breakup.isLoading ? (
        <div className="h-16 animate-pulse rounded-lg bg-soft" />
      ) : breakup.isError ? (
        <p className="text-sm text-danger">
          {breakup.error instanceof ApiV2Error ? breakup.error.message : "Could not load the payment details."}
        </p>
      ) : !breakup.data || breakup.data.length === 0 ? (
        <p className="text-sm text-ink-2">No payment has been made for this order yet.</p>
      ) : (
        <ol className="space-y-4">
          {breakup.data.map((attempt) => (
            <li key={attempt.id} aria-label={`Payment ${attempt.ref_payment_id}`} className="rounded-xl border border-line p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-ink">
                    {method(attempt)} · <span className="tabular-nums">{rupees(attempt.amount)}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-ink-3">
                    {formatWhen(attempt.created_at)} · Payment ID <span className="font-mono">{attempt.ref_payment_id}</span>
                  </p>
                </div>
                <span className={cn("inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold", STATUS[attempt.status].tone)}>
                  {STATUS[attempt.status].label}
                </span>
              </div>
              {attempt.failure_reason && (
                <p className="mt-2 text-sm text-danger">{attempt.failure_reason}</p>
              )}
              {attempt.shares.length > 0 && (
                <dl className="mt-3 space-y-1 border-t border-line pt-3 text-sm" aria-label="Split across items">
                  {attempt.shares.map((share) => (
                    <div key={share.line_item_id} className="flex justify-between text-ink-2">
                      <dt>{share.name}</dt>
                      <dd className="tabular-nums">{rupees(share.amount)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
